import React, {useContext, Fragment, useMemo, useEffect} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {useTranslate} from '@computerrock/formation-i18n';
import {FileAssetsDropZone, FileAssetsInput, useFileAssets} from '@ace-de/eua-file-assets';
import {Form, TextAreaField, useStyles, Checkbox, StepperContext, Info, Circle, LoaderSpinner} from '@ace-de/ui-components';
import {Icon, uploadIcon, addIcon, deleteIcon, fileIcon, photoIcon} from '@ace-de/ui-components/icons';
import {MIAAttachment} from '@ace-de/eua-entity-types';
import FormButtons from './FormButtons';
import {formStepNames} from '../constants/forms';
import * as invoiceSubmissionActionTypes from '../invoiceSubmissionActionTypes';
import {isInvoiceValidationError} from '../utils/validation';
import {invoiceSubmissionServiceTypes} from '../constants/invoiceSubmissionServices';
import {isPdfMIMEType} from '../../utils/files';
import config from '../../config';
import styles from './InvoiceDocumentForm.module.scss';

const allowedMimeTypes = [
    'image/jpeg',
    'image/png',
    'application/pdf',
    'image/heif',
];

const InvoiceDocumentForm = props => {
    const {cx} = useStyles(props, styles);
    const {invoiceSubmissionDraft, updateInvoiceSubmissionDraft, cancelInvoiceSubmissionDraft} = props;
    const {isOverlay, setInvoiceDocumentFormFiles} = props;
    const {createTranslateShorthand} = useTranslate();
    const translateForm = createTranslateShorthand('invoice_document_form');
    const {selectStep, currentStep} = useContext(StepperContext);
    const {configure: configureFileAssetsService, fileList, deleteFile} = useFileAssets({
        service: 'miaFileAssetsService',
        fileList: 'invoice-documents',
    });

    useEffect(() => {
        configureFileAssetsService({
            allowedMIMETypes: allowedMimeTypes,
            maxFileSize: config.MAX_FILE_SIZE,
            maxNumberOfFiles: config.MAX_NUMBER_OF_FILES,
            isNonBearerAuthorization: true,
            uploadedFilesCount: invoiceSubmissionDraft?.attachmentsDetails?.attachments
                ? invoiceSubmissionDraft.attachmentsDetails.attachments.length
                : 0,
        });
    }, [configureFileAssetsService, invoiceSubmissionDraft?.attachmentsDetails?.attachments]);

    const files = useMemo(() => {
        return fileList.fileAssets ? [...fileList.fileAssets.values()] : [];
    }, [fileList.fileAssets]);

    useEffect(() => {
        if (!files || files.length === 0 || typeof setInvoiceDocumentFormFiles !== 'function') return;

        setInvoiceDocumentFormFiles(files);
    }, [files, setInvoiceDocumentFormFiles]);

    const uploadedDocumentTypes = useMemo(() => {
        let hasRoadKillService = false;
        let hasBrokenGlassOrMartenBiteServices = false;
        invoiceSubmissionDraft?.services?.forEach(service => {
            if (service?.type === invoiceSubmissionServiceTypes.BROKEN_GLASS) {
                hasBrokenGlassOrMartenBiteServices = true;
                return;
            }
            if (service?.type === invoiceSubmissionServiceTypes.MARTEN_BITE) {
                hasBrokenGlassOrMartenBiteServices = true;
                return;
            }
            if (service?.type === invoiceSubmissionServiceTypes.ELEMENTAL_DAMAGE) {
                hasBrokenGlassOrMartenBiteServices = true;
                return;
            }
            if (service?.type === invoiceSubmissionServiceTypes.ROADKILL) hasRoadKillService = true;
        });
        return {
            invoiceUploaded: {
                value: !hasRoadKillService && !hasBrokenGlassOrMartenBiteServices,
                label: translateForm('checkbox_label.receipt', {
                    isRequired: '*',
                }),
            },
            repairInvoiceUploaded: {
                value: true,
                label: translateForm('checkbox_label.repair_receipt', {
                    isRequired: hasRoadKillService || hasBrokenGlassOrMartenBiteServices ? '*' : '',
                }),
            },
            carInsuranceUploaded: {
                value: hasRoadKillService || hasBrokenGlassOrMartenBiteServices,
                label: translateForm('checkbox_label.premium_car_insurance', {
                    isRequired: '*',
                }),
            },
            wildlifeDamageCertUploaded: {
                value: hasRoadKillService,
                label: translateForm('checkbox_label.wildlife_damage', {
                    isRequired: '*',
                }),
            },
        };
    }, [invoiceSubmissionDraft?.services, translateForm]);

    const uploadedFiles = useMemo(() => {
        const attachments = invoiceSubmissionDraft?.attachmentsDetails?.attachments || [];
        return [
            ...(files ?? []),
            ...(!!attachments && attachments.filter(attachment => !(
                files && files.some(file => file.id === attachment.id)
            ))),
        ];
    }, [files, invoiceSubmissionDraft?.attachmentsDetails?.attachments]);

    if (!invoiceSubmissionDraft) return null;

    const handleOnSubmit = formValues => {
        if (!formValues) return;
        const {
            invoiceUploaded,
            repairInvoiceUploaded,
            carInsuranceUploaded,
            wildlifeDamageCertUploaded,
            ...restFormValues
        } = formValues;
        const attachments = uploadedFiles.map(file => new MIAAttachment().fromDTO(file));
        updateInvoiceSubmissionDraft({
            invoiceSubmissionDraftData: {
                attachmentsDetails: {
                    attachments,
                    invoiceUploaded: !!invoiceUploaded,
                    repairInvoiceUploaded: !!repairInvoiceUploaded,
                    carInsuranceUploaded: !!carInsuranceUploaded,
                    wildlifeDamageCertUploaded: !!wildlifeDamageCertUploaded,
                },
                ...restFormValues,
            },
            selectStep,
            currentStep,
        });
    };

    const handleOnDeleteFile = fileAsset => {
        if (!fileAsset || !fileAsset.id) return;
        const attachments = invoiceSubmissionDraft?.attachmentsDetails?.attachments || [];
        const isFileOnInvoiceSubmissionDraft = attachments.find(attachment => attachment.id === fileAsset.id);
        if (isFileOnInvoiceSubmissionDraft) {
            updateInvoiceSubmissionDraft({
                invoiceSubmissionDraftData: {
                    attachmentsDetails: {
                        ...invoiceSubmissionDraft.attachmentsDetails,
                        attachments: attachments.filter(attachment => attachment.id !== fileAsset.id),
                    },
                },
            });
        }

        deleteFile(fileAsset);
    };

    const handleOnClose = formValues => {
        let invoiceSubmissionDraftData;
        if (formValues) {
            const {
                invoiceUploaded,
                repairInvoiceUploaded,
                carInsuranceUploaded,
                wildlifeDamageCertUploaded,
                ...restFormValues
            } = formValues;
            const attachments = uploadedFiles.map(file => new MIAAttachment().fromDTO(file));
            invoiceSubmissionDraftData = {
                attachmentsDetails: {
                    attachments,
                    invoiceUploaded: !!invoiceUploaded,
                    repairInvoiceUploaded: !!repairInvoiceUploaded,
                    carInsuranceUploaded: !!carInsuranceUploaded,
                    wildlifeDamageCertUploaded: !!wildlifeDamageCertUploaded,
                },
                ...restFormValues,
            };
        }

        cancelInvoiceSubmissionDraft({invoiceSubmissionDraftData, currentStep});
    };

    return (
        <Form name="invoiceDocument" onSubmit={handleOnSubmit}>
            {formValues => (
                <Fragment>
                    <h2
                        className={cx([
                            'global!ace-u-typography--variant-h2',
                            'global!ace-u-margin--small-bottom-24',
                        ])}
                    >
                        {translateForm('title.add_invoice')}
                    </h2>
                    <p
                        className={cx([
                            'global!ace-u-margin--small-bottom-16',
                            'global!ace-u-margin--large-bottom-32',
                            'global!ace-u-typography--variant-body',
                        ])}
                    >
                        {translateForm('invoice.description')}
                    </p>
                    <p
                        className={cx([
                            'global!ace-u-margin--small-bottom-24',
                            'global!ace-u-margin--large-bottom-32',
                            'global!ace-u-typography--variant-caption',
                        ])}
                    >
                        {translateForm('invoice.format')}
                    </p>
                    {fileList.errors.length > 0 && fileList.errors.map((error, index) => (
                        <Info
                            key={index}
                            isWarning
                            className={cx([
                                'global!ace-u-margin--small-bottom-16',
                                'global!ace-u-margin--large-bottom-32',
                            ])}
                        >
                            {translateForm(`file_upload_error.${error.errorType.toLowerCase()}`)}
                        </Info>
                    ))}
                    <div
                        className={cx([
                            'global!ace-u-display--large-block',
                            'global!ace-u-display--small-none',
                        ])}
                    >
                        <FileAssetsDropZone
                            service="miaFileAssetsService"
                            fileList="invoice-documents"
                            className={cx([
                                'global!ace-u-padding--32',
                                'global!ace-u-margin--bottom-32',
                                'global!ace-u-width--full',
                                'global!ace-u-flex',
                                'global!ace-u-flex--direction-column',
                                'global!ace-u-flex--justify-center',
                                'global!ace-u-flex--align-center',
                            ])}
                        >
                            <div
                                className={cx([
                                    'global!ace-u-display--large-flex',
                                    'global!ace-u-display--small-none',
                                    'global!ace-u-flex--direction-column',
                                    'global!ace-u-flex--justify-center',
                                    'global!ace-u-flex--align-center',
                                ])}
                            >
                                <Circle
                                    className={cx([
                                        'ace-c-circle--medium',
                                        'global!ace-u-margin--bottom-32',
                                    ])}
                                >
                                    <Icon
                                        icon={uploadIcon}
                                        className={cx([
                                            'ace-c-icon--xl',
                                            'ace-c-icon--color-highlight',
                                            'ace-c-icon--no-margin',
                                        ])}
                                    />
                                </Circle>
                                <p className={cx('global!ace-u-typography--variant-body-medium')}>
                                    {translateForm('file_dropzone_label.document_upload')}
                                </p>
                                <p
                                    className={cx([
                                        'global!ace-u-margin--8-0-16',
                                        'global!ace-u-typography--variant-caption',
                                        'global!ace-u-typography--color-medium-emphasis',
                                    ])}
                                >
                                    {translateForm('file_dropzone_description.correct_format')}
                                </p>
                            </div>
                            <FileAssetsInput
                                service="miaFileAssetsService"
                                fileList="invoice-documents"
                                className={cx([
                                    'global!ace-u-margin--16-0',
                                    'ace-c-file-input--no-chrome',
                                ])}
                            >
                                <div
                                    className={cx([
                                        'global!ace-u-flex',
                                        'global!ace-u-flex--align-center',
                                        'global!ace-u-typography--variant-highlight',
                                        'global!ace-u-typography--color-highlighted',
                                        'global!ace-u-cursor--pointer',
                                    ])}
                                >
                                    <Icon
                                        icon={addIcon}
                                        className={cx([
                                            'global!ace-u-margin--right-8',
                                            'ace-c-icon--color-highlight',
                                        ])}
                                    />
                                    {translateForm('file_input_label.search_documents')}
                                </div>
                            </FileAssetsInput>
                        </FileAssetsDropZone>
                    </div>
                    <div
                        className={cx([
                            'global!ace-u-display--large-none',
                            'global!ace-u-display--small-flex',
                            'global!ace-u-margin--bottom-32',
                            'global!ace-u-flex--align-center',
                            'global!ace-u-flex--justify-space-between',
                        ])}
                    >
                        <FileAssetsInput
                            service="miaFileAssetsService"
                            fileList="invoice-documents"
                            className={cx([
                                'global!ace-u-margin--16-0',
                            ])}
                        >
                            <div
                                className={cx([
                                    'global!ace-u-flex',
                                    'global!ace-u-flex--align-center',
                                    'global!ace-u-typography--variant-highlight',
                                    'global!ace-u-cursor--pointer',
                                ])}
                            >
                                <p
                                    className={cx([
                                        'global!ace-u-padding--16',
                                        'global!ace-u-margin--right-32',
                                    ])}
                                >
                                    {translateForm('file_input_label.choose_documents')}
                                </p>
                                <div
                                    className={cx([
                                        'global!ace-u-padding--0-8',
                                        'global!ace-u-width--64',
                                        'global!ace-u-height--64',
                                        'global!ace-u-flex',
                                        'global!ace-u-flex--align-center',
                                        'global!ace-u-flex--justify-center',
                                        'global!ace-u-box-skin--color-primary',
                                    ])}
                                >
                                    <Icon
                                        icon={addIcon}
                                        className={cx([
                                            'ace-c-icon--l',
                                            'ace-c-icon--color-contrast',
                                        ])}
                                    />
                                </div>
                            </div>
                        </FileAssetsInput>
                    </div>
                    {files.length > 0 && files.some(fileAsset => fileAsset.isUploadPending) && (
                        <div
                            className={cx([
                                'global!ace-u-width--full',
                                'global!ace-u-height--full',
                                'global!ace-u-flex',
                                'global!ace-u-flex--align-center',
                                'global!ace-u-flex--justify-center',
                                'global!ace-u-position--fixed',
                                'global!ace-u-position--top-0',
                                'global!ace-u-position--left-0',
                                'global!ace-u-z-index--lock',
                                'global!ace-u-box-skin--color-background-modal',
                            ])}
                        >
                            <LoaderSpinner />
                        </div>
                    )}
                    {Object.values(uploadedFiles).length > 0 && (
                        <div className={cx(['global!ace-u-margin--bottom-32'])}>
                            <p
                                className={cx([
                                    'global!ace-u-margin--bottom-16',
                                    'global!ace-u-typography--variant-body-medium',
                                ])}
                            >
                                {translateForm('title.added_documents')}
                            </p>
                            {Object.values(uploadedFiles).map(fileAsset => (
                                <div
                                    key={fileAsset.id}
                                    className={cx([
                                        'global!ace-u-padding--16',
                                        'global!ace-u-margin--bottom-8',
                                        'global!ace-u-grid',
                                        'global!ace-u-box-skin--pending',
                                    ])}
                                >
                                    <div
                                        className={cx([
                                            'global!ace-u-grid-column--span-11',
                                            'global!ace-u-flex',
                                            'global!ace-u-flex--align-center',
                                        ])}
                                    >
                                        <Icon
                                            className={cx('ace-c-icon--color-highlight')}
                                            icon={isPdfMIMEType(fileAsset.fileType) ? fileIcon : photoIcon}
                                        />
                                        <p
                                            className={cx([
                                                'global!ace-u-margin--left-8',
                                                'global!ace-u-typography--color-highlighted',
                                                'ace-c-invoice-document-form__file-name',
                                            ])}
                                        >
                                            {fileAsset.fileName}
                                        </p>
                                    </div>
                                    <div
                                        className={cx([
                                            'global!ace-u-flex',
                                            'global!ace-u-flex--align-center',
                                            'global!ace-u-flex--justify-content-flex-end',
                                        ])}
                                    >
                                        <span
                                            data-qa={`invoice-document-delete-file-${fileAsset.id}`}
                                            onClick={() => handleOnDeleteFile(fileAsset)}
                                        >
                                            <Icon
                                                icon={deleteIcon}
                                                className={cx([
                                                    'ace-c-icon--xs',
                                                    'ace-c-icon--color-medium-emphasis',
                                                ])}
                                            />
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <h3
                        className={cx([
                            'global!ace-u-typography--variant-h3',
                            'global!ace-u-margin--small-bottom-16',
                        ])}
                    >
                        {translateForm('title.remarks')}
                    </h3>
                    <TextAreaField
                        placeholder={translateForm('text_area_placeholder.invoicing_remarks')}
                        name="invoicingRemarks"
                        value={invoiceSubmissionDraft.invoicingRemarks || ''}
                        maxLength={250}
                        isResizable={false}
                        className={cx([
                            'ace-c-text-area--small',
                            'global!ace-u-margin--small-bottom-32',
                            'global!ace-u-width--full',
                        ])}
                        label={translateForm('text_area_label.invoicing_remarks')}
                    />
                    <div className={cx('global!ace-u-margin--small-bottom-16')}>
                        {Object.entries(uploadedDocumentTypes)
                            .filter(([fieldName, valueObj]) => !!valueObj.value)
                            .map(([fieldName, valueObj]) => (
                                <div
                                    key={`${fieldName}Checkbox`}
                                    className={cx(['global!ace-u-margin--small-bottom-16'])}
                                >
                                    <Checkbox
                                        name={fieldName}
                                        value={true}
                                        isSelected={invoiceSubmissionDraft?.attachmentsDetails
                                            ? !!invoiceSubmissionDraft.attachmentsDetails[fieldName] : false}
                                    >
                                        {valueObj.label}
                                    </Checkbox>
                                </div>
                            ))}
                    </div>
                    {!isOverlay && (
                        <FormButtons
                            formName={formStepNames.INVOICE_FORM}
                            isSubmitDisabled={isInvoiceValidationError(
                                formValues || invoiceSubmissionDraft?.attachmentsDetails,
                                uploadedFiles,
                            )}
                            handleOnClose={() => handleOnClose(formValues)}
                        />
                    )}
                </Fragment>
            )}
        </Form>
    );
};

InvoiceDocumentForm.propTypes = {
    updateInvoiceSubmissionDraft: PropTypes.func.isRequired,
    invoiceSubmissionDraft: PropTypes.object,
    isOverlay: PropTypes.bool,
    cancelInvoiceSubmissionDraft: PropTypes.func.isRequired,
    setInvoiceDocumentFormFiles: PropTypes.func,
};

InvoiceDocumentForm.defaultProps = {
    invoiceSubmissionDraft: null,
    isOverlay: false,
    setInvoiceDocumentFormFiles: null,
};

const mapStateToProps = state => {
    return {
        invoiceSubmissionDraft: state.invoiceSubmissions.invoiceSubmissionDraft,
    };
};

const mapDispatchToProps = dispatch => ({
    updateInvoiceSubmissionDraft: payload => dispatch({
        type: invoiceSubmissionActionTypes.UPDATE_INVOICE_SUBMISSION_DRAFT,
        payload,
    }),
    cancelInvoiceSubmissionDraft: payload => dispatch({
        type: invoiceSubmissionActionTypes.INITIATE_INVOICE_SUBMISSION_CANCEL_FLOW,
        payload,
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceDocumentForm);
